import { useState, useEffect, FunctionComponent, PropsWithChildren, useContext, useMemo } from "react";

import { useTheme } from "@mui/styles";
import { StripePaymentMethod, Utils, OrganizationCountry, PaymentMethod } from "@simplyk/common";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";

import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { useCurrentUserContext } from "../contexts/CurrentUserContext";
import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { FrontendTicketingContext } from "../contexts/FrontendTicketingContext";
import { useLocaleContext } from "../contexts/LocaleContext";
import { ExpressCheckoutWallet, StripeContext } from "../contexts/StripeContext";
import { listPaymentMethods } from "../helpers/payment-method";
import { useTranslate } from "../hooks/useTranslate";

import { getStripeAppearance } from "@/forms-design-system/FormPaymentMethod/FormPaymentMethodTokens";
import { serializeLocale } from "@/helpers/stripe";

interface StripeContextProviderProps {
  country?: OrganizationCountry | null;
  isEmbed: boolean;
}

const WAITING_TIME_BEFORE_DISPLAY_STRIPE_ERROR = 2000;

export const STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD: Record<PaymentMethodWithStripe, StripePaymentMethod> = {
  [PaymentMethod.Card]: "card",
  [PaymentMethod.Ach]: "us_bank_account",
};

export type PaymentMethodWithStripe = PaymentMethod.Card | PaymentMethod.Ach;

export const isPaymentMethodWithStripe = (pm: PaymentMethod): pm is PaymentMethodWithStripe => {
  return pm === PaymentMethod.Card || pm === PaymentMethod.Ach;
};

const StripeError = () => {
  const [displayError, setDisplayError] = useState(false);

  const { t } = useTranslate();

  useEffect(() => {
    const errorTimer: NodeJS.Timeout = setTimeout(
      () => setDisplayError(true),
      WAITING_TIME_BEFORE_DISPLAY_STRIPE_ERROR
    );
    return () => {
      clearTimeout(errorTimer);
    };
  }, []);

  if (!displayError) {
    return <></>;
  }
  return <ErrorMessage message={t("common", "unableToLoadStripe")} />;
};

export const StripeProvider: FunctionComponent<PropsWithChildren<StripeContextProviderProps>> = ({
  children,
  isEmbed,
}) => {
  const {
    organization,
    currency,
    displayedFormAmount,
    displayedFormAmountWithTip,
    stripeRecurrenceInterval,
    formData,
    formType,
    areBankPaymentMethodsAllowed,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    prioritizeBankPayment,
    displayExpressCheckout,
    expressCheckoutIsSelected,
    isExpressCheckoutLoading,
    paymentMode,
    isAuction,
    isPreviewTemplateMode,
    isFreeTicketing,
  } = useContext(FrontendFormContext);

  const theme = useTheme();

  const { selectedOccurrenceWithRates } = useContext(FrontendTicketingContext);
  const { locale } = useLocaleContext();

  const stripeInstance = useMemo(() => {
    const stripePublicApiKey =
      organization.country === OrganizationCountry.Canada
        ? (process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_CA as string)
        : organization.country === OrganizationCountry.GreatBritain
          ? (process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_UK as string)
          : (process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_USA as string);

    return loadStripe(stripePublicApiKey, { locale: serializeLocale(locale) });
  }, [locale, organization.country]);

  const [expressCheckoutWallet, setExpressCheckoutWallet] = useState<ExpressCheckoutWallet | undefined>(undefined);

  const paymentMethods = useMemo(
    () =>
      listPaymentMethods({
        formData,
        stripeRecurrenceInterval,
        selectedOccurrence: selectedOccurrenceWithRates?.occurrence,
        organizationCountry: organization.country,
        totalAmount: displayedFormAmount,
        formType,
        areBankPaymentMethodsAllowed,
        orgCardMaximumAmount: organization.cardMaximumAmount || Infinity,
        isAuction,
        isStripeCustomAccountActive: Boolean(organization.isStripeCustomAccountActive),
        isPreviewTemplateMode,
        isEmbed,
        isFreeTicketing,
      }),
    [
      displayedFormAmount,
      formData,
      formType,
      isAuction,
      areBankPaymentMethodsAllowed,
      organization.cardMaximumAmount,
      organization.country,
      organization.isStripeCustomAccountActive,
      selectedOccurrenceWithRates?.occurrence,
      stripeRecurrenceInterval,
      isPreviewTemplateMode,
      isEmbed,
      isFreeTicketing,
    ]
  );

  const stripePaymentMethodTypes: StripePaymentMethod[] = useMemo(
    () =>
      paymentMethods
        .map((pm) => {
          if (!isPaymentMethodWithStripe(pm)) {
            return;
          }
          return STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD[pm];
        })
        .filter(Utils.notEmpty),
    [paymentMethods]
  );

  const stripePaymentMethods: PaymentMethod[] = useMemo(
    () => paymentMethods.filter((pm) => isPaymentMethodWithStripe(pm)),
    [paymentMethods]
  );

  const zeffyPaymentMethods = useMemo(
    () =>
      paymentMethods
        .filter((pm) => !isPaymentMethodWithStripe(pm) && pm !== PaymentMethod.ApplePayOrGooglePay)
        .filter(Utils.notEmpty),
    [paymentMethods]
  );

  const isPadOrAch = selectedPaymentMethod === PaymentMethod.Ach || selectedPaymentMethod === PaymentMethod.Pad;
  const options: StripeElementsOptions = {
    mode: paymentMode,
    ...(paymentMode !== "setup"
      ? { amount: displayedFormAmountWithTip && displayedFormAmountWithTip >= 0 ? displayedFormAmountWithTip : 100 }
      : {}),
    currency,
    appearance: getStripeAppearance(theme),
    paymentMethodTypes: stripePaymentMethodTypes,
    // setupFutureUsage: "off_session" as const,
    paymentMethodOptions: {
      us_bank_account: {
        financial_connections: { permissions: ["payment_method" as const] },
      },
    },
    paymentMethodCreation: "manual" as const,
    setupFutureUsage: isPadOrAch ? ("off_session" as const) : null,
  };

  const hasPad = paymentMethods.includes(PaymentMethod.Pad);
  const hasAch = paymentMethods.includes(PaymentMethod.Ach);

  // If the currently selected payment method is not available, we need to select another one
  useEffect(() => {
    // We ignore this logic when express checkout is supported and selected
    if (expressCheckoutIsSelected && displayExpressCheckout) {
      return;
    }
    if (
      selectedPaymentMethod &&
      !zeffyPaymentMethods.includes(selectedPaymentMethod) &&
      !stripePaymentMethods.includes(selectedPaymentMethod)
    ) {
      if (prioritizeBankPayment && (hasPad || hasAch)) {
        setSelectedPaymentMethod(hasPad ? PaymentMethod.Pad : PaymentMethod.Ach);
      } else if (stripePaymentMethods.length > 0) {
        setSelectedPaymentMethod(stripePaymentMethods[0]);
      } else if (zeffyPaymentMethods.length > 0) {
        setSelectedPaymentMethod(zeffyPaymentMethods[0]);
      } else {
        setSelectedPaymentMethod(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod, stripePaymentMethods, zeffyPaymentMethods, isExpressCheckoutLoading]);

  // Set card as default payment method if express checkout isn't supported or if in previewTemplateMode, no payment method is selected
  // and card is available
  useEffect(() => {
    if (
      !selectedPaymentMethod &&
      (!displayExpressCheckout || isPreviewTemplateMode) &&
      !isExpressCheckoutLoading &&
      stripePaymentMethods.includes(PaymentMethod.Card)
    ) {
      setSelectedPaymentMethod(PaymentMethod.Card);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpressCheckoutLoading]);

  try {
    return (
      <StripeContext.Provider
        value={{
          hasPad,
          hasAch,
          stripePaymentMethodTypes,
          stripePaymentMethods,
          zeffyPaymentMethods,
          expressCheckoutWallet,
          setExpressCheckoutWallet,
        }}
      >
        <Elements key={locale} stripe={stripeInstance} options={options}>
          {children}
        </Elements>
      </StripeContext.Provider>
    );
  } catch {
    return <StripeError />;
  }
};

export const StripeCustomAccountProvider = ({ children }: PropsWithChildren) => {
  const { organization } = useCurrentUserContext();
  const { locale } = useLocaleContext();

  const stripeInstance = useMemo(() => {
    const stripePublicApiKey =
      organization?.country === OrganizationCountry.Canada
        ? (process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_CA as string)
        : organization?.country === OrganizationCountry.GreatBritain
          ? (process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_UK as string)
          : (process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_USA as string);

    return loadStripe(stripePublicApiKey, {
      locale: serializeLocale(locale),
      stripeAccount: organization?.stripeCustomAccountId || "",
    });
  }, [locale, organization?.country, organization?.stripeCustomAccountId]);

  try {
    return (
      <Elements key={locale} stripe={stripeInstance}>
        {children}
      </Elements>
    );
  } catch {
    return <StripeError />;
  }
};
